import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { 
  FaUserTie, FaLaptopMedical, FaCertificate, 
  FaCarBattery, FaDollarSign, FaHeadset,
  FaCheckCircle, FaStar
} from 'react-icons/fa';

const WhyChooseUs = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <FaUserTie size={48} />,
      title: t('why.experienced'),
      description: t('why.experiencedDesc'),
      details: ['Certified by leading automotive bodies', 'Years of hands-on experience', 'Continuous training & upskilling']
    },
    {
      icon: <FaLaptopMedical size={48} />,
      title: t('why.equipment'),
      description: t('why.equipmentDesc'),
      details: ['Latest diagnostic computers', 'OEM-grade tools', 'Specialized hybrid equipment']
    },
    {
      icon: <FaCertificate size={48} />,
      title: t('why.specialist'),
      description: t('why.specialistDesc'),
      details: ['Mercedes-Benz, BMW, Audi', 'Volkswagen, Mini Cooper', 'European brand expertise']
    },
    {
      icon: <FaCarBattery size={48} />,
      title: t('why.hybridExpert'),
      description: t('why.hybridExpertDesc'),
      details: ['Battery diagnosis & repair', 'High voltage system testing', 'Toyota & Lexus hybrid certified']
    },
    {
      icon: <FaDollarSign size={48} />,
      title: t('why.pricing'),
      description: t('why.pricingDesc'),
      details: ['Honest, upfront quotes', 'No hidden fees', 'Detailed invoices']
    },
    {
      icon: <FaHeadset size={48} />,
      title: t('why.support'),
      description: t('why.supportDesc'),
      details: ['Warranty on workmanship', 'Follow-up service calls', 'Lifetime customer support']
    }
  ];

  return (
    <div className="min-h-screen" data-testid="why-choose-page">
      <section className="relative h-[400px] flex items-center bg-cover bg-center"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/44zt2kut_Workshop5.jpeg)' }}
      >
        <div className="absolute inset-0 bg-dark-navy/85"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full text-center">
          <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            WHY CHOOSE US
          </h1>
          <p className="text-lg text-gray-200">Six reasons we are Muar's trusted automotive partner</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                data-testid={`why-feature-${index}`}
                className="bg-light-grey p-8 border border-gray-200 hover:shadow-lg hover:border-royal-blue transition-all"
              >
                <div className="text-royal-blue mb-4">{feature.icon}</div>
                <h3 className="font-cabinet font-bold text-xl text-dark-navy mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{feature.description}</p>
                <ul className="space-y-2 pt-4 border-t border-gray-300">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <FaCheckCircle className="text-royal-blue mr-2 mt-1 flex-shrink-0" size={12} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-cabinet font-extrabold text-4xl text-blue-400 mb-2">10+</div>
              <div className="text-sm text-gray-300 uppercase tracking-wide">Years Experience</div>
            </div>
            <div>
              <div className="font-cabinet font-extrabold text-4xl text-blue-400 mb-2">1000+</div>
              <div className="text-sm text-gray-300 uppercase tracking-wide">Vehicles Serviced</div>
            </div>
            <div>
              <div className="font-cabinet font-extrabold text-4xl text-blue-400 mb-2">10</div>
              <div className="text-sm text-gray-300 uppercase tracking-wide">Brands Supported</div>
            </div>
            <div>
              <div className="font-cabinet font-extrabold text-4xl text-blue-400 mb-2 flex items-center justify-center gap-1">
                3.9<FaStar size={24} className="text-yellow-400" />
              </div>
              <div className="text-sm text-gray-300 uppercase tracking-wide">Google Rating</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-cabinet font-bold text-3xl text-dark-navy mb-4">Experience Our Service Today</h2>
          <p className="text-base mb-8 text-gray-600">Join hundreds of satisfied customers in Muar and Johor</p>
          <Link 
            to="/contact"
            data-testid="why-cta-contact"
            className="inline-block px-8 py-3 bg-royal-blue text-white font-cabinet font-bold hover:bg-[#002277] transition-colors uppercase tracking-wide"
          >
            Book Appointment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
